import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, setAuthToken, getAuthToken } from "@/lib/api";

// Auth token is memory-only (see /app/frontend/src/lib/api.js) to close
// the XSS token-exfiltration exposure that comes with localStorage. Trade-off:
// a page reload clears the token; the user re-verifies via a 6-digit code
// (single email, no password). That's acceptable UX for an ungated catalog
// where authentication only gates checkout submission and account views.

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = still checking, false = anonymous, object = authenticated user
  const [user, setUser] = useState(null);
  const [token, setTokenState] = useState(() => getAuthToken());

  const loadUser = useCallback(async () => {
    // No token in memory means no prior sign-in this session → anonymous.
    if (!getAuthToken()) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      // Token invalid or expired — wipe it and revert to anonymous.
      setAuthToken(null);
      setTokenState(null);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const requestCode = async (email) => {
    try {
      const { data } = await api.post("/auth/request-code", { email });
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const completeLogin = (data) => {
    setAuthToken(data.token);
    setTokenState(data.token);
    setUser(data.user);
  };

  const verifyCode = async (email, code) => {
    try {
      const { data } = await api.post("/auth/verify-code", { email, code });
      completeLogin(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const verifyLink = async (linkToken) => {
    try {
      const { data } = await api.get("/auth/verify-link", { params: { token: linkToken } });
      completeLogin(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  // Google (Emergent Auth) callback: called by AuthCallback after
  // reading `session_id` from the URL fragment. Backend calls
  // Emergent's `/session-data` endpoint (never the frontend, per
  // playbook), upserts the user, and returns the same {token, user}
  // shape the email OTP flow uses so the memory-token pattern works.
  const exchangeGoogleSession = async (sessionId) => {
    try {
      const { data } = await api.post(
        "/auth/google/session",
        null,
        { headers: { "X-Session-Id": sessionId } }
      );
      completeLogin(data);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const logout = () => {
    setAuthToken(null);
    setTokenState(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, token, requestCode, verifyCode, verifyLink, exchangeGoogleSession, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
